import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LinkIcon from '@mui/icons-material/Link';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { saudeService } from '../../services/api';

/**
 * Cobertura Vacinal oficial (painel do Ministério da Saúde, por município de residência).
 * Abas: Crianças e Gestantes (painéis por residência) e Adolescentes (HPV e Meningo ACWY, por coorte).
 *
 * Os números vêm do painel do MS (infoms.saude.gov.br) e não do PEC: o % oficial conta doses
 * aplicadas em qualquer lugar sobre a população-alvo do SINASC — ver docs/PLANO_COBERTURA_VACINAL.md.
 * O status (verde/amarelo/vermelho) vem pronto do backend, com o mesmo limiar do PDF público.
 */

type Status = 'atingida' | 'alerta' | 'abaixo' | 'sem_dados';

interface Item {
  chave: string;
  rotulo: string;
  meta: number;
  numerador: number | null;
  denominador: number | null;
  cobertura: number | null;
  faltam_pp: number | null;
  status: Status;
  anterior: { cobertura: number | null };
  variacao_pp: number | null;
}

interface Painel {
  chave: string;
  titulo: string;
  dados_ate: string | null;
  atualizado_em: string | null;
  grupos: { chave: string; rotulo: string; itens: Item[] }[];
  erro?: string;
}

interface Dados {
  municipio: { id: number; nome: string; uf: string | null };
  ano: number;
  paineis: Painel[];
  resumo: { na_meta: number; total: number; piores: { chave: string; rotulo: string; cobertura: number; faltam_pp: number }[] };
  consultado_em: string;
  desatualizado?: boolean;
}

/** Linha do modo "Somente PEC" devolvida pelo backend (numerador; o denominador e o oficial). */
interface LinhaPec {
  chave: string;
  numerador: number;
  transcricoes: number;
  nao_residentes: number;
  regra_validada: boolean;
}

interface RespostaPec {
  elegivel: boolean;
  motivo: 'ok' | 'sem_conexao' | 'dwh';
  em_calculo: boolean;
  pode_recalcular: boolean;
  calculado_em: string | null;
  modo_pec: string | null;
  itens: LinhaPec[];
}

/** Item exibido no modo PEC: cobertura do PEC + referencia do oficial. */
interface ItemPec extends Item {
  oficial: number | null;
  numeradorMs: number | null;
  transcricoes: number;
  naoResidentes: number;
  regraValidada: boolean;
}

type Fonte = 'ms' | 'pec';
const CHAVE_FONTE = 'cobertura:fonte';

/** Mesmo limiar do backend e do PDF (LIMIAR_ALERTA_PP em paineis.config.ts). */
const LIMIAR_ALERTA_PP = 5;

function statusDe(cobertura: number | null, meta: number): Status {
  if (cobertura === null) return 'sem_dados';
  if (cobertura >= meta) return 'atingida';
  return meta - cobertura <= LIMIAR_ALERTA_PP ? 'alerta' : 'abaixo';
}

const arred2 = (v: number) => Math.round(v * 100) / 100;

function lerFonte(): Fonte {
  try {
    return sessionStorage.getItem(CHAVE_FONTE) === 'pec' ? 'pec' : 'ms';
  } catch {
    return 'ms';
  }
}

function gravarFonte(f: Fonte) {
  try {
    sessionStorage.setItem(CHAVE_FONTE, f);
  } catch {
    /* sessionStorage indisponivel: a escolha vale so ate recarregar */
  }
}

/** Remonta os paineis com o numerador do PEC sobre o denominador oficial. */
function montarDadosPec(dados: Dados, pec: RespostaPec): Dados {
  const porChave = new Map(pec.itens.map((l) => [l.chave, l]));
  const paineis = dados.paineis.map((p) => ({
    ...p,
    grupos: p.grupos.map((g) => ({
      ...g,
      itens: g.itens.map((i): ItemPec => {
        const l = porChave.get(i.chave);
        const numerador = l ? l.numerador : null;
        const cobertura = numerador !== null && i.denominador ? arred2((numerador / i.denominador) * 100) : null;
        const status = statusDe(cobertura, i.meta);
        return {
          ...i,
          numerador,
          cobertura,
          status,
          faltam_pp: cobertura === null ? null : arred2(Math.max(0, i.meta - cobertura)),
          anterior: { cobertura: null },
          variacao_pp: null,
          oficial: i.cobertura,
          numeradorMs: i.numerador,
          transcricoes: l?.transcricoes ?? 0,
          naoResidentes: l?.nao_residentes ?? 0,
          regraValidada: l?.regra_validada ?? false,
        };
      }),
    })),
  }));
  const itens = paineis.flatMap((p) => p.grupos.flatMap((g) => g.itens)).filter((i) => i.cobertura !== null);
  const piores = itens
    .filter((i) => i.status !== 'atingida')
    .sort((a, b) => (b.faltam_pp || 0) - (a.faltam_pp || 0))
    .slice(0, 3)
    .map((i) => ({ chave: i.chave, rotulo: i.rotulo, cobertura: i.cobertura!, faltam_pp: i.faltam_pp! }));
  return { ...dados, paineis, resumo: { na_meta: itens.filter((i) => i.status === 'atingida').length, total: itens.length, piores } };
}

const STATUS_COR: Record<Status, string> = {
  atingida: '#1b7f3b',
  alerta: '#d4a106',
  abaixo: '#d64545',
  sem_dados: '#9e9e9e',
};

const STATUS_TEXTO: Record<Status, string> = {
  atingida: '#1b7f3b',
  alerta: '#a87c00',
  abaixo: '#d64545',
  sem_dados: '#757575',
};

const ANO_MINIMO = 2015;

const fmtPct = (v: number | null) =>
  v === null ? '—' : `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const fmtPp = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v: number | null) => (v === null ? '—' : Math.round(v).toLocaleString('pt-BR'));
const fmtData = (iso: string | null) => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const CardImunizante: React.FC<{ item: Item; anoAnterior: number }> = ({ item, anoAnterior }) => {
  const theme = useTheme();
  const cor = STATUS_COR[item.status];
  const preenchido = item.cobertura === null ? 0 : Math.max(0, Math.min(100, item.cobertura));
  const direita =
    item.status === 'sem_dados'
      ? 'Sem dados'
      : item.status === 'atingida'
        ? 'Meta atingida'
        : `Faltam ${fmtPp(item.faltam_pp || 0)} p.p.`;

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${cor}`, height: '100%' }}>
      <CardContent sx={{ py: 1.25, px: 1.75, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.rotulo}
          </Typography>
          <Tooltip title={`${fmtInt(item.numerador)} doses de ${fmtInt(item.denominador)} (população-alvo)`}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: STATUS_TEXTO[item.status], lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {fmtPct(item.cobertura)}
            </Typography>
          </Tooltip>
        </Box>
        <Box sx={{ position: 'relative', height: 8, my: 0.75, borderRadius: 4, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${preenchido}%`, borderRadius: 4, bgcolor: cor }} />
          <Tooltip title={`Meta ${item.meta}%`}>
            <Box sx={{ position: 'absolute', left: `calc(${item.meta}% - 1px)`, top: -3, width: 2, height: 14, bgcolor: 'text.primary' }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            Meta {item.meta}% · {fmtInt(item.numerador)} de {fmtInt(item.denominador)} · {anoAnterior}: {fmtPct(item.anterior.cobertura)}
            {item.variacao_pp !== null && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  ml: 0.5,
                  fontWeight: 600,
                  color: item.variacao_pp >= 0 ? STATUS_COR.atingida : STATUS_COR.abaixo,
                }}
              >
                {item.variacao_pp >= 0 ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                {item.variacao_pp >= 0 ? '+' : '–'}
                {fmtPp(Math.abs(item.variacao_pp))} p.p.
              </Box>
            )}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: STATUS_TEXTO[item.status] }}>
            {direita}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const CardPec: React.FC<{ item: ItemPec }> = ({ item }) => {
  const theme = useTheme();
  const cor = STATUS_COR[item.status];
  const preenchido = item.cobertura === null ? 0 : Math.max(0, Math.min(100, item.cobertura));
  const completude =
    item.numerador !== null && item.numeradorMs ? `${fmtPct(arred2((item.numerador / item.numeradorMs) * 100))}` : '—';
  const direita =
    item.status === 'sem_dados'
      ? 'Sem cálculo'
      : item.status === 'atingida'
        ? 'Meta atingida'
        : `Faltam ${fmtPp(item.faltam_pp || 0)} p.p.`;

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${cor}`, height: '100%' }}>
      <CardContent sx={{ py: 1.25, px: 1.75, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.rotulo}
            </Typography>
            {!item.regraValidada && (
              <Tooltip title="Regra inferida: o Ministério da Saúde não publicou a ficha deste indicador. O número ainda está sendo conferido com o oficial.">
                <Chip size="small" icon={<ScienceOutlinedIcon />} label="Regra em validação" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            )}
          </Box>
          <Tooltip title={`${fmtInt(item.numerador)} vacinados no PEC de ${fmtInt(item.denominador)} (população-alvo oficial)`}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: STATUS_TEXTO[item.status], lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {fmtPct(item.cobertura)}
            </Typography>
          </Tooltip>
        </Box>
        <Box sx={{ position: 'relative', height: 8, my: 0.75, borderRadius: 4, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
          <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${preenchido}%`, borderRadius: 4, bgcolor: cor }} />
          <Tooltip title={`Meta ${item.meta}%`}>
            <Box sx={{ position: 'absolute', left: `calc(${item.meta}% - 1px)`, top: -3, width: 2, height: 14, bgcolor: 'text.primary' }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.disabled">
            Oficial (MS): {fmtPct(item.oficial)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: STATUS_TEXTO[item.status] }}>
            {direita}
          </Typography>
        </Box>
        <Tooltip
          title={`Fora do número: ${fmtInt(item.transcricoes)} com transcrição de caderneta e ${fmtInt(item.naoResidentes)} sem residência no município.`}
        >
          <Typography variant="caption" color="text.secondary" component="div">
            Registrado no PEC: {fmtInt(item.numerador)} de {fmtInt(item.numeradorMs)} vacinados do MS ({completude})
          </Typography>
        </Tooltip>
      </CardContent>
    </Card>
  );
};

const SaudeCoberturaVacinal: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo, hasPermission } = useAuth();
  const anoAtual = new Date().getFullYear();

  const [ano, setAno] = useState<number>(anoAtual);
  const [aba, setAba] = useState(0);
  const [dados, setDados] = useState<Dados | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);

  const [link, setLink] = useState<{ token: string } | null>(null);
  const [podeGerar, setPodeGerar] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [confirmarNovo, setConfirmarNovo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const [fonte, setFonteState] = useState<Fonte>(lerFonte);
  const [pec, setPec] = useState<RespostaPec | null>(null);
  const [recargaPec, setRecargaPec] = useState(0);
  const setFonte = (f: Fonte) => {
    setFonteState(f);
    gravarFonte(f);
  };

  const anos = useMemo(() => {
    const lista: number[] = [];
    for (let a = anoAtual; a >= ANO_MINIMO; a--) lista.push(a);
    return lista;
  }, [anoAtual]);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setDados(null);
      return;
    }
    setLoading(true);
    setErro(null);
    saudeService
      .getCoberturaVacinal(municipioAtivo.id, ano)
      .then((res: Dados) => setDados(res))
      .catch((err: any) => {
        setDados(null);
        setErro(err.response?.data?.message || 'Não foi possível carregar a cobertura vacinal.');
      })
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, ano, recarga]);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .getCoberturaVacinalLink(municipioAtivo.id)
      .then((res: any) => {
        setLink(res.link);
        setPodeGerar(!!res.pode_gerar);
      })
      .catch(() => {
        setLink(null);
        setPodeGerar(false);
      });
  }, [municipioAtivo?.id]);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .getCoberturaVacinalPec(municipioAtivo.id, ano)
      .then((res: RespostaPec) => setPec(res))
      .catch(() => setPec(null));
  }, [municipioAtivo?.id, ano, recargaPec]);

  // Enquanto o recalculo roda em segundo plano, consulta de novo a cada 5 s.
  useEffect(() => {
    if (!pec?.em_calculo) return;
    const t = setTimeout(() => setRecargaPec((r) => r + 1), 5000);
    return () => clearTimeout(t);
  }, [pec]);

  const pecDisponivel = !!pec?.elegivel;
  const motivoSemPec = pecDisponivel
    ? ''
    : pec?.motivo === 'dwh'
      ? 'Este município usa o Data Warehouse; o cálculo pelo PEC vale só para conexão direta ao PEC.'
      : pec?.motivo === 'sem_conexao'
        ? 'Este município não tem conexão com o PEC cadastrada (Municípios → Conexões).'
        : 'Carregando…';
  const modoPec = fonte === 'pec' && pecDisponivel;
  const dadosPec = useMemo(() => (dados && pec?.calculado_em ? montarDadosPec(dados, pec) : null), [dados, pec]);
  const dadosVisao = modoPec ? dadosPec : dados;

  const recalcularPec = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      await saudeService.recalcularCoberturaVacinalPec(municipioAtivo.id);
      setAviso('Recálculo iniciado. Os números aparecem em instantes.');
      // Marca "em cálculo" já: o backend toma a trava de forma assíncrona e uma consulta imediata
      // ainda poderia vir sem ela, encerrando o acompanhamento. O polling de 5 s confere de verdade.
      setPec((p) => (p ? { ...p, em_calculo: true } : p));
    } catch (err: any) {
      setAviso(err.response?.data?.message || 'Não foi possível iniciar o recálculo.');
    }
  }, [municipioAtivo?.id]);

  const [gerandoPdf, setGerandoPdf] = useState(false);
  const pdfIndisponivel = loading || !dados || (modoPec && !pec?.calculado_em);
  const baixarPdf = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    setGerandoPdf(true);
    try {
      await saudeService.exportarCoberturaVacinalPdf(municipioAtivo.id, ano, modoPec ? 'pec' : 'ms');
    } catch {
      setAviso('Não foi possível gerar o PDF.');
    } finally {
      setGerandoPdf(false);
    }
  }, [municipioAtivo?.id, ano, modoPec]);

  const urlPublica = link ? saudeService.urlPublicaCoberturaVacinal(link.token) : '';

  const gerarLink = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    setGerando(true);
    try {
      const res = await saudeService.gerarCoberturaVacinalLink(municipioAtivo.id);
      setLink(res.link);
      setAviso(link ? 'Novo link gerado. O link anterior deixou de funcionar.' : 'Link público gerado.');
    } catch (err: any) {
      setAviso(err.response?.data?.message || 'Não foi possível gerar o link.');
    } finally {
      setGerando(false);
      setConfirmarNovo(false);
    }
  }, [municipioAtivo?.id, link]);

  const copiarLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(urlPublica);
      setAviso('Link copiado.');
    } catch {
      setAviso('Não foi possível copiar automaticamente. Selecione o link e copie.');
    }
  }, [urlPublica]);

  const atualizarDados = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    await saudeService.atualizarCoberturaVacinal(municipioAtivo.id).catch(() => undefined);
    setRecarga((r) => r + 1);
  }, [municipioAtivo?.id]);

  const painel = dadosVisao?.paineis[aba];

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um município para ver a cobertura vacinal.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VaccinesIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Cobertura Vacinal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {modoPec
                ? 'Somente aplicações registradas no PEC · mesma regra e mesma população-alvo do Ministério da Saúde'
                : 'Dados oficiais do Ministério da Saúde · cobertura por município de residência'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Fonte:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={modoPec ? 'pec' : 'ms'}
              onChange={(_, v) => v && setFonte(v)}
              aria-label="Fonte dos dados"
            >
              <ToggleButton value="ms">Ministério da Saúde (oficial)</ToggleButton>
              <Tooltip title={motivoSemPec || ''} disableHoverListener={!motivoSemPec}>
                <span>
                  <ToggleButton value="pec" disabled={!pecDisponivel} selected={modoPec} onChange={() => setFonte('pec')}>
                    Somente aplicações do PEC
                  </ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
          {hasPermission('SYSTEM_ADMIN') && !modoPec && (
            <Tooltip title="Buscar novamente no painel do Ministério da Saúde">
              <span>
                <Button size="small" startIcon={<RefreshIcon />} onClick={atualizarDados} disabled={loading}>
                  Atualizar
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={modoPec ? 'PDF com as aplicações do PEC (uso interno)' : 'PDF com os dados oficiais do Ministério da Saúde'}>
            <span>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} size="small" color="error" onClick={baixarPdf} disabled={pdfIndisponivel || gerandoPdf}>
                {gerandoPdf ? 'Gerando…' : 'PDF'}
              </Button>
            </span>
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="cv-ano">Ano</InputLabel>
            <Select labelId="cv-ano" label="Ano" value={ano} onChange={(e) => setAno(Number(e.target.value))}>
              {anos.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}
      {dados?.desatualizado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          O painel do Ministério da Saúde está indisponível no momento. Exibindo os últimos dados obtidos em {fmtData(dados.consultado_em)}.
        </Alert>
      )}

      {modoPec && !pec?.calculado_em && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            pec?.pode_recalcular ? (
              <Button color="inherit" size="small" onClick={recalcularPec} disabled={pec.em_calculo}>
                {pec.em_calculo ? 'Calculando…' : 'Calcular agora'}
              </Button>
            ) : undefined
          }
        >
          {pec?.em_calculo
            ? 'Calculando a cobertura com as aplicações do PEC…'
            : `A cobertura do PEC de ${ano} ainda não foi calculada. O cálculo roda toda madrugada.`}
        </Alert>
      )}

      {/* Resumo */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Imunizantes na meta
              </Typography>
              {loading || !dadosVisao ? (
                <Skeleton width={120} height={48} />
              ) : (
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {dadosVisao.resumo.na_meta}{' '}
                  <Typography component="span" variant="h6" color="text.secondary">
                    de {dadosVisao.resumo.total}
                  </Typography>
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: alpha(STATUS_COR.abaixo, 0.05) }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Mais distantes da meta
              </Typography>
              {loading || !dadosVisao ? (
                <Skeleton height={60} />
              ) : dadosVisao.resumo.piores.length === 0 ? (
                <Typography variant="body2" sx={{ color: STATUS_COR.atingida, fontWeight: 600 }}>
                  Todos os imunizantes atingiram a meta.
                </Typography>
              ) : (
                dadosVisao.resumo.piores.map((p, i) => (
                  <Box key={p.chave} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {i + 1}. {p.rotulo}
                    </Typography>
                    <Typography variant="body2" sx={{ color: STATUS_COR.abaixo, whiteSpace: 'nowrap' }}>
                      {fmtPct(p.cobertura)} · faltam {fmtPp(p.faltam_pp)} p.p.
                    </Typography>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Abas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <Tabs value={aba} onChange={(_, v) => setAba(v)}>
          <Tab label="Crianças" />
          <Tab label="Gestantes" />
          <Tab label="Adolescentes" />
        </Tabs>
        {painel && !painel.erro && !modoPec && (
          <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
            Doses aplicadas até {fmtData(painel.dados_ate)}
          </Typography>
        )}
        {modoPec && pec?.calculado_em && (
          <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
            Calculado em {new Date(pec.calculado_em).toLocaleString('pt-BR')}
            {pec.pode_recalcular && (
              <Button size="small" sx={{ ml: 1 }} onClick={recalcularPec} disabled={pec.em_calculo}>
                {pec.em_calculo ? 'Calculando…' : 'Recalcular'}
              </Button>
            )}
          </Typography>
        )}
      </Box>

      {/* Legenda */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {[
          { cor: STATUS_COR.atingida, texto: 'Meta atingida' },
          { cor: STATUS_COR.alerta, texto: 'Até 5 p.p. da meta' },
          { cor: STATUS_COR.abaixo, texto: 'Mais de 5 p.p. abaixo' },
        ].map((l) => (
          <Box key={l.texto} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: l.cor }} />
            <Typography variant="caption" color="text.secondary">
              {l.texto}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 2, height: 14, bgcolor: 'text.primary' }} />
          <Typography variant="caption" color="text.secondary">
            Meta
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {modoPec ? 'Cinza = cobertura oficial do Ministério da Saúde' : 'Seta = variação em relação ao ano anterior'}
        </Typography>
      </Box>

      {loading && (
        <Grid container spacing={1.5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={84} />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && painel?.erro && <Alert severity="warning">{painel.erro}</Alert>}
      {!loading && dadosVisao && !painel && (
        <Alert severity="info">Dados desta aba ainda não disponíveis para o município. Tente novamente em instantes.</Alert>
      )}

      {!loading &&
        painel &&
        !painel.erro &&
        painel.grupos.map((g) => (
          <Box key={g.chave} sx={{ mb: 2 }}>
            {painel.grupos.length > 1 && (
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {g.rotulo}
              </Typography>
            )}
            <Grid container spacing={1.5}>
              {g.itens.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.chave}>
                  {modoPec ? <CardPec item={item as ItemPec} /> : <CardImunizante item={item} anoAnterior={dados!.ano - 1} />}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

      {/* Link público */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinkIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Link público
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            PDF sempre atualizado com a cobertura do município, para publicar no site da prefeitura ou nas redes sociais.
            Qualquer pessoa com o link consegue abrir, sem login.
          </Typography>
          {modoPec && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0, fontSize: '0.8125rem', '& .MuiAlert-icon': { py: 0.5 } }}>
              O link público mostra apenas os dados oficiais do Ministério da Saúde.
            </Alert>
          )}
          {link ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                opacity: modoPec ? 0.5 : 1,
                '& .MuiButton-root': { whiteSpace: 'nowrap', flexShrink: 0 },
              }}
            >
              {/* Campo da URL com largura contida: o link e longo e, com flex: 1, empurrava os botoes para a linha de baixo. */}
              <TextField
                size="small"
                value={urlPublica}
                InputProps={{ readOnly: true, sx: { fontSize: '0.8125rem' } }}
                sx={{ flex: '1 1 260px', maxWidth: 460 }}
                onFocus={(e) => e.target.select()}
              />
              <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copiarLink} disabled={modoPec}>
                Copiar link
              </Button>
              {podeGerar && (
                <Button color="warning" startIcon={<AutorenewIcon />} onClick={() => setConfirmarNovo(true)} disabled={gerando || modoPec}>
                  Gerar novo link
                </Button>
              )}
            </Box>
          ) : podeGerar ? (
            <Button variant="contained" startIcon={<LinkIcon />} onClick={gerarLink} disabled={gerando || modoPec}>
              Gerar link
            </Button>
          ) : (
            <Alert severity="info">O link público ainda não foi gerado. Peça a um administrador do município.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Rodapé */}
      {modoPec && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
          Modo PEC: numerador = residentes que completaram o esquema com doses registradas no PEC do município, pela regra do
          Ministério da Saúde; denominador = população-alvo oficial. Transcrições de caderneta e pessoas sem residência no
          município ficam de fora (veja o detalhe passando o mouse em cada card).
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: modoPec ? 1 : 2 }}>
        Fonte: Ministério da Saúde – RNDS, Painel de Cobertura Vacinal por Residência (infoms.saude.gov.br). Cobertura = doses
        aplicadas ÷ população-alvo.
        {painel?.atualizado_em && ` Atualização do painel em ${painel.atualizado_em}.`}
      </Typography>

      <Dialog open={confirmarNovo} onClose={() => setConfirmarNovo(false)}>
        <DialogTitle>Gerar novo link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            O link atual deixa de funcionar na hora. Quem já publicou o link antigo (site da prefeitura, redes sociais) precisará
            trocar pelo novo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarNovo(false)}>Cancelar</Button>
          <Button color="warning" variant="contained" onClick={gerarLink} disabled={gerando}>
            Gerar novo link
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso} autoHideDuration={4000} onClose={() => setAviso(null)} message={aviso} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default SaudeCoberturaVacinal;
